import { useState, useMemo } from "react";
import { Shield, Plus, X, AlertTriangle, CheckCircle2, Clock, Target, ChevronDown, ChevronUp, Trash2, Edit3, MessageSquare, Send, Download } from "lucide-react";
import { toast } from "sonner";
import { useRiskData, type Risk, type RiskComment } from "@/hooks/useRiskData";
import { useAuth } from "@/contexts/AuthContext";
import { exportRisksPdf } from "@/lib/pdfExport";

const CATEGORIES = ["operational", "security", "compliance", "environmental", "financial", "reputational"];
const SEVERITIES = ["critical", "high", "medium", "low"];
const STATUSES = ["open", "mitigating", "monitoring", "closed"];
const MITIGATION_STATUSES = ["not_started", "in_progress", "completed", "overdue"];

const severityColors: Record<string, string> = {
  critical: "app-red",
  high: "app-red",
  medium: "app-gold",
  low: "app-green",
};

const statusColors: Record<string, string> = {
  open: "app-red",
  mitigating: "app-gold",
  monitoring: "app-blue",
  closed: "app-green",
};

const mitigationColors: Record<string, string> = {
  not_started: "app-text-muted",
  in_progress: "app-gold",
  completed: "app-green",
  overdue: "app-red",
};

interface Props {
  sites: { id: string; name: string }[];
  mode: "business_owner" | "site_owner";
  siteIds?: string[];
}

const RiskManagement = ({ sites, mode, siteIds }: Props) => {
  const { user, profile, role } = useAuth();
  const { risks, comments, loading, addRisk, updateRisk, deleteRisk, addComment } = useRiskData(
    mode === "site_owner" ? siteIds : undefined
  );
  const [subTab, setSubTab] = useState<"register" | "matrix" | "mitigation">("register");
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedRisk, setExpandedRisk] = useState<string | null>(null);
  const [editingRisk, setEditingRisk] = useState<string | null>(null);
  const [commentText, setCommentText] = useState("");
  const [filter, setFilter] = useState<{ category: string; severity: string; status: string }>({
    category: "all",
    severity: "all",
    status: "all",
  });

  // New risk form
  const [newRisk, setNewRisk] = useState({
    title: "",
    description: "",
    category: "operational",
    severity: "medium",
    likelihood: 3,
    impact: 3,
    site_id: sites[0]?.id || "",
    mitigation_plan: "",
    assigned_to: "",
    mitigation_deadline: "",
  });

  const filteredRisks = useMemo(() => {
    return risks.filter((r) => {
      if (filter.category !== "all" && r.category !== filter.category) return false;
      if (filter.severity !== "all" && r.severity !== filter.severity) return false;
      if (filter.status !== "all" && r.status !== filter.status) return false;
      return true;
    });
  }, [risks, filter]);

  // Matrix data
  const matrixData = useMemo(() => {
    const grid: Risk[][][] = Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => []));
    risks.filter(r => r.status !== "closed").forEach((r) => {
      grid[5 - r.impact][r.likelihood - 1].push(r);
    });
    return grid;
  }, [risks]);

  const matrixCellColor = (impact: number, likelihood: number): string => {
    const score = impact * likelihood;
    if (score >= 15) return "hsl(var(--app-red) / 0.3)";
    if (score >= 8) return "hsl(var(--app-gold) / 0.25)";
    if (score >= 4) return "hsl(var(--app-blue) / 0.15)";
    return "hsl(var(--app-green) / 0.15)";
  };

  const handleAddRisk = async () => {
    if (!newRisk.title.trim()) {
      toast.error("Title is required");
      return;
    }
    try {
      await addRisk({
        ...newRisk,
        site_id: newRisk.site_id || null,
        mitigation_deadline: newRisk.mitigation_deadline || null,
        created_by: user?.id,
      } as any);
      toast.success("Risk added");
      setShowAddForm(false);
      setNewRisk({ title: "", description: "", category: "operational", severity: "medium", likelihood: 3, impact: 3, site_id: sites[0]?.id || "", mitigation_plan: "", assigned_to: "", mitigation_deadline: "" });
    } catch {
      toast.error("Failed to add risk");
    }
  };

  const handleUpdateRisk = async (id: string, updates: Partial<Risk>) => {
    try {
      await updateRisk(id, updates);
      toast.success("Risk updated");
      setEditingRisk(null);
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDeleteRisk = async (id: string) => {
    try {
      await deleteRisk(id);
      toast.success("Risk removed");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleAddComment = async (riskId: string) => {
    if (!commentText.trim()) return;
    try {
      await addComment({
        risk_id: riskId,
        user_id: user?.id || "",
        user_name: profile?.full_name || "Unknown",
        user_role: role || "unknown",
        content: commentText.trim(),
      });
      setCommentText("");
      toast.success("Comment added");
    } catch {
      toast.error("Failed to add comment");
    }
  };

  const getSiteName = (siteId: string | null) => {
    if (!siteId) return "Unassigned";
    return sites.find((s) => s.id === siteId)?.name || "Unknown";
  };

  const riskCommentsForId = (riskId: string) => comments.filter((c) => c.risk_id === riskId);

  // Stats
  const openRisks = risks.filter((r) => r.status === "open").length;
  const criticalRisks = risks.filter((r) => r.severity === "critical" && r.status !== "closed").length;
  const mitigating = risks.filter((r) => r.mitigation_status === "in_progress").length;
  const avgScore = risks.length > 0 ? Math.round(risks.reduce((s, r) => s + r.risk_score, 0) / risks.length) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 rounded-full border-2 border-[hsl(var(--app-gold))] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Open Risks", value: openRisks, color: "app-red", icon: AlertTriangle },
          { label: "Critical", value: criticalRisks, color: "app-red", icon: Shield },
          { label: "Mitigating", value: mitigating, color: "app-gold", icon: Target },
          { label: "Avg Score", value: avgScore, color: "app-blue", icon: Target },
        ].map((stat) => (
          <div key={stat.label} className="app-card rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <stat.icon className={`w-4 h-4 text-[hsl(var(--${stat.color}))]`} />
              <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--app-text-muted))]">{stat.label}</p>
            </div>
            <p className={`font-bebas text-2xl text-[hsl(var(--${stat.color}))]`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-2">
          {(["register", "matrix", "mitigation"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setSubTab(t)}
              className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                subTab === t
                  ? "bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))]"
                  : "border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))]"
              }`}
            >
              {t === "register" ? "Risk Register" : t === "matrix" ? "Risk Matrix" : "Mitigation Tracker"}
            </button>
          ))}
        </div>
        <button
          onClick={() => exportRisksPdf(filteredRisks)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-secondary))] text-xs hover:border-[hsl(var(--app-gold))] hover:text-[hsl(var(--app-gold))] transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> Export PDF
        </button>
      </div>

      {/* ===== RISK REGISTER ===== */}
      {subTab === "register" && (
        <div className="space-y-3">
          {/* Filters + Add button */}
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={filter.category}
              onChange={(e) => setFilter({ ...filter, category: e.target.value })}
              className="text-xs px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-secondary))] outline-none"
            >
              <option value="all">All Categories</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
              ))}
            </select>
            <select
              value={filter.severity}
              onChange={(e) => setFilter({ ...filter, severity: e.target.value })}
              className="text-xs px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-secondary))] outline-none"
            >
              <option value="all">All Severities</option>
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="text-xs px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-secondary))] outline-none"
            >
              <option value="all">All Statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
            {mode === "business_owner" && (
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-xs font-semibold hover:brightness-110 transition-all"
              >
                <Plus className="w-3.5 h-3.5" /> Add Risk
              </button>
            )}
          </div>

          {/* Add risk form */}
          {showAddForm && mode === "business_owner" && (
            <div className="app-card rounded-xl p-5 space-y-3">
              <div className="flex items-center justify-between mb-2">
                <p className="font-bebas text-lg text-[hsl(var(--app-text-primary))]">NEW RISK</p>
                <button onClick={() => setShowAddForm(false)} className="text-[hsl(var(--app-text-muted))]"><X className="w-4 h-4" /></button>
              </div>
              <input
                value={newRisk.title}
                onChange={(e) => setNewRisk({ ...newRisk, title: e.target.value })}
                placeholder="Risk title"
                className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none"
              />
              <textarea
                value={newRisk.description}
                onChange={(e) => setNewRisk({ ...newRisk, description: e.target.value })}
                placeholder="Description"
                rows={2}
                className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none resize-none"
              />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Site</label>
                  <select value={newRisk.site_id} onChange={(e) => setNewRisk({ ...newRisk, site_id: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-xs text-[hsl(var(--app-text-secondary))] outline-none">
                    {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Category</label>
                  <select value={newRisk.category} onChange={(e) => setNewRisk({ ...newRisk, category: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-xs text-[hsl(var(--app-text-secondary))] outline-none">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Severity</label>
                  <select value={newRisk.severity} onChange={(e) => setNewRisk({ ...newRisk, severity: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-xs text-[hsl(var(--app-text-secondary))] outline-none">
                    {SEVERITIES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Assigned To</label>
                  <input value={newRisk.assigned_to} onChange={(e) => setNewRisk({ ...newRisk, assigned_to: e.target.value })} placeholder="Name" className="w-full mt-1 px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-xs text-[hsl(var(--app-text-secondary))] outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Likelihood (1-5)</label>
                  <input type="number" min={1} max={5} value={newRisk.likelihood} onChange={(e) => setNewRisk({ ...newRisk, likelihood: Math.min(5, Math.max(1, +e.target.value)) })} className="w-full mt-1 px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-xs text-[hsl(var(--app-text-secondary))] outline-none" />
                </div>
                <div>
                  <label className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Impact (1-5)</label>
                  <input type="number" min={1} max={5} value={newRisk.impact} onChange={(e) => setNewRisk({ ...newRisk, impact: Math.min(5, Math.max(1, +e.target.value)) })} className="w-full mt-1 px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-xs text-[hsl(var(--app-text-secondary))] outline-none" />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Deadline</label>
                  <input type="date" value={newRisk.mitigation_deadline} onChange={(e) => setNewRisk({ ...newRisk, mitigation_deadline: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-xs text-[hsl(var(--app-text-secondary))] outline-none" />
                </div>
              </div>
              <textarea
                value={newRisk.mitigation_plan}
                onChange={(e) => setNewRisk({ ...newRisk, mitigation_plan: e.target.value })}
                placeholder="Mitigation plan..."
                rows={2}
                className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none resize-none"
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-[hsl(var(--app-text-muted))]">Risk Score: <span className="font-bebas text-lg text-[hsl(var(--app-gold))]">{newRisk.likelihood * newRisk.impact}</span></p>
                <button onClick={handleAddRisk} className="px-6 py-2.5 rounded-lg bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-xs font-semibold hover:brightness-110">Add Risk</button>
              </div>
            </div>
          )}

          {/* Risk list */}
          {filteredRisks.length === 0 ? (
            <div className="app-card rounded-xl p-12 text-center">
              <Shield className="w-10 h-10 mx-auto mb-3 text-[hsl(var(--app-text-muted))]" />
              <p className="text-sm text-[hsl(var(--app-text-muted))]">No risks found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredRisks.map((risk) => {
                const isExpanded = expandedRisk === risk.id;
                const riskComments = riskCommentsForId(risk.id);
                const sColor = severityColors[risk.severity] || "app-text-muted";
                const stColor = statusColors[risk.status] || "app-text-muted";
                return (
                  <div key={risk.id} className="app-card rounded-xl overflow-hidden">
                    <button
                      onClick={() => setExpandedRisk(isExpanded ? null : risk.id)}
                      className="w-full p-4 flex items-center gap-3 text-left"
                    >
                      <div className={`w-2 h-2 rounded-full bg-[hsl(var(--${sColor}))]`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[hsl(var(--app-text-primary))] truncate">{risk.title}</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[hsl(var(--${sColor})/0.15)] text-[hsl(var(--${sColor}))]">{risk.severity}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[hsl(var(--${stColor})/0.15)] text-[hsl(var(--${stColor}))]">{risk.status}</span>
                          <span className="text-[10px] text-[hsl(var(--app-text-muted))]">{risk.category}</span>
                          <span className="text-[10px] text-[hsl(var(--app-text-muted))]">{getSiteName(risk.site_id)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-center">
                          <p className="font-bebas text-lg text-[hsl(var(--app-gold))]">{risk.risk_score}</p>
                          <p className="text-[8px] text-[hsl(var(--app-text-muted))] uppercase">Score</p>
                        </div>
                        {riskComments.length > 0 && (
                          <div className="flex items-center gap-1 text-[hsl(var(--app-text-muted))]">
                            <MessageSquare className="w-3 h-3" />
                            <span className="text-[10px]">{riskComments.length}</span>
                          </div>
                        )}
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-[hsl(var(--app-text-muted))]" /> : <ChevronDown className="w-4 h-4 text-[hsl(var(--app-text-muted))]" />}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-[hsl(var(--app-border-subtle))] p-4 space-y-3">
                        {risk.description && <p className="text-xs text-[hsl(var(--app-text-secondary))]">{risk.description}</p>}
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div>
                            <p className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase">Likelihood</p>
                            <p className="font-mono-data text-sm text-[hsl(var(--app-text-primary))]">{risk.likelihood}/5</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase">Impact</p>
                            <p className="font-mono-data text-sm text-[hsl(var(--app-text-primary))]">{risk.impact}/5</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase">Assigned</p>
                            <p className="text-xs text-[hsl(var(--app-text-secondary))]">{risk.assigned_to || "—"}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase">Deadline</p>
                            <p className="text-xs text-[hsl(var(--app-text-secondary))]">{risk.mitigation_deadline ? new Date(risk.mitigation_deadline).toLocaleDateString() : "—"}</p>
                          </div>
                        </div>

                        {risk.mitigation_plan && (
                          <div>
                            <p className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase mb-1">Mitigation Plan</p>
                            <p className="text-xs text-[hsl(var(--app-text-secondary))] bg-[hsl(var(--app-surface-2))] rounded-lg p-3">{risk.mitigation_plan}</p>
                          </div>
                        )}

                        {/* Security operator controls */}
                        {mode === "business_owner" && (
                          <div className="flex flex-wrap gap-2">
                            <select
                              value={risk.status}
                              onChange={(e) => handleUpdateRisk(risk.id, { status: e.target.value })}
                              className="text-xs px-3 py-1.5 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-secondary))] outline-none"
                            >
                              {STATUSES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                            </select>
                            <select
                              value={risk.mitigation_status}
                              onChange={(e) => handleUpdateRisk(risk.id, { mitigation_status: e.target.value })}
                              className="text-xs px-3 py-1.5 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-secondary))] outline-none"
                            >
                              {MITIGATION_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ").charAt(0).toUpperCase() + s.replace("_", " ").slice(1)}</option>)}
                            </select>
                            <button onClick={() => handleDeleteRisk(risk.id)} className="ml-auto p-1.5 rounded-lg text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-red))] hover:bg-[hsl(var(--app-red)/0.1)]">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}

                        {/* Comments section */}
                        <div className="border-t border-[hsl(var(--app-border-subtle))] pt-3">
                          <p className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase mb-2 flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" /> Comments ({riskComments.length})
                          </p>
                          {riskComments.length > 0 && (
                            <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                              {riskComments.map((c) => (
                                <div key={c.id} className="bg-[hsl(var(--app-surface-2))] rounded-lg p-3">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-[10px] font-semibold text-[hsl(var(--app-text-primary))]">{c.user_name}</span>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))]">{c.user_role?.replace("_", " ")}</span>
                                    <span className="text-[10px] text-[hsl(var(--app-text-muted))] ml-auto">{new Date(c.created_at).toLocaleString()}</span>
                                  </div>
                                  <p className="text-xs text-[hsl(var(--app-text-secondary))]">{c.content}</p>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <input
                              value={expandedRisk === risk.id ? commentText : ""}
                              onChange={(e) => setCommentText(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && handleAddComment(risk.id)}
                              placeholder="Add a comment..."
                              className="flex-1 px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-xs text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none"
                            />
                            <button onClick={() => handleAddComment(risk.id)} className="p-2 rounded-lg bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] hover:brightness-110">
                              <Send className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===== RISK MATRIX ===== */}
      {subTab === "matrix" && (
        <div className="app-card rounded-xl p-5">
          <p className="font-bebas text-lg text-[hsl(var(--app-text-primary))] mb-4">RISK HEATMAP</p>
          <div className="flex gap-2">
            <div className="flex flex-col justify-between py-1 pr-2">
              {[5, 4, 3, 2, 1].map((i) => (
                <div key={i} className="h-14 flex items-center">
                  <span className="text-[10px] text-[hsl(var(--app-text-muted))] w-4 text-right">{i}</span>
                </div>
              ))}
              <p className="text-[10px] text-[hsl(var(--app-text-muted))] text-center mt-1 -rotate-90 origin-center translate-y-[-40px] translate-x-[-12px] whitespace-nowrap">IMPACT →</p>
            </div>
            <div className="flex-1">
              <div className="grid grid-cols-5 gap-1">
                {matrixData.map((row, ri) =>
                  row.map((cell, ci) => (
                    <div
                      key={`${ri}-${ci}`}
                      className="h-14 rounded-lg flex items-center justify-center relative border border-[hsl(var(--app-border-subtle))]"
                      style={{ backgroundColor: matrixCellColor(5 - ri, ci + 1) }}
                      title={`Impact: ${5 - ri}, Likelihood: ${ci + 1} — ${cell.length} risk(s)`}
                    >
                      {cell.length > 0 && (
                        <div className="flex flex-col items-center">
                          <span className="font-bebas text-sm text-[hsl(var(--app-text-primary))]">{cell.length}</span>
                          <span className="text-[8px] text-[hsl(var(--app-text-muted))]">risk{cell.length > 1 ? "s" : ""}</span>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
              <div className="flex justify-between mt-2">
                {[1, 2, 3, 4, 5].map((l) => (
                  <span key={l} className="text-[10px] text-[hsl(var(--app-text-muted))] w-[20%] text-center">{l}</span>
                ))}
              </div>
              <p className="text-[10px] text-[hsl(var(--app-text-muted))] text-center mt-1">LIKELIHOOD →</p>
            </div>
          </div>
          <div className="flex gap-4 mt-4 justify-center">
            {[
              { label: "Critical (15-25)", color: "hsl(var(--app-red) / 0.3)" },
              { label: "High (8-14)", color: "hsl(var(--app-gold) / 0.25)" },
              { label: "Medium (4-7)", color: "hsl(var(--app-blue) / 0.15)" },
              { label: "Low (1-3)", color: "hsl(var(--app-green) / 0.15)" },
            ].map((l) => (
              <div key={l.label} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: l.color }} />
                <span className="text-[10px] text-[hsl(var(--app-text-muted))]">{l.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== MITIGATION TRACKER ===== */}
      {subTab === "mitigation" && (
        <div className="space-y-2">
          {risks.filter(r => r.status !== "closed").length === 0 ? (
            <div className="app-card rounded-xl p-12 text-center">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-[hsl(var(--app-green))]" />
              <p className="text-sm text-[hsl(var(--app-text-muted))]">No active risks to track</p>
            </div>
          ) : (
            risks
              .filter((r) => r.status !== "closed")
              .sort((a, b) => b.risk_score - a.risk_score)
              .map((risk) => {
                const mColor = mitigationColors[risk.mitigation_status] || "app-text-muted";
                const progress =
                  risk.mitigation_status === "completed" ? 100 :
                  risk.mitigation_status === "in_progress" ? 50 :
                  risk.mitigation_status === "overdue" ? 75 : 0;
                const isOverdue = risk.mitigation_deadline && new Date(risk.mitigation_deadline) < new Date() && risk.mitigation_status !== "completed";

                return (
                  <div key={risk.id} className="app-card rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-2 h-2 rounded-full bg-[hsl(var(--${severityColors[risk.severity] || "app-text-muted"}))]`} />
                      <p className="text-sm font-semibold text-[hsl(var(--app-text-primary))] flex-1">{risk.title}</p>
                      <span className="font-bebas text-lg text-[hsl(var(--app-gold))]">{risk.risk_score}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full bg-[hsl(var(--${mColor})/0.15)] text-[hsl(var(--${mColor}))]`}>
                        {risk.mitigation_status.replace("_", " ")}
                      </span>
                      {risk.assigned_to && (
                        <span className="text-[10px] text-[hsl(var(--app-text-muted))]">→ {risk.assigned_to}</span>
                      )}
                      {risk.mitigation_deadline && (
                        <span className={`text-[10px] flex items-center gap-1 ${isOverdue ? "text-[hsl(var(--app-red))]" : "text-[hsl(var(--app-text-muted))]"}`}>
                          <Clock className="w-3 h-3" />
                          {new Date(risk.mitigation_deadline).toLocaleDateString()}
                          {isOverdue && " (OVERDUE)"}
                        </span>
                      )}
                    </div>
                    <div className="h-2 rounded-full bg-[hsl(var(--app-border-subtle))] overflow-hidden">
                      <div
                        className={`h-full rounded-full bg-[hsl(var(--${mColor}))] transition-all`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    {risk.mitigation_plan && (
                      <p className="text-[10px] text-[hsl(var(--app-text-muted))] mt-2">{risk.mitigation_plan}</p>
                    )}
                    {mode === "business_owner" && (
                      <div className="flex gap-2 mt-3">
                        <select
                          value={risk.mitigation_status}
                          onChange={(e) => handleUpdateRisk(risk.id, { mitigation_status: e.target.value })}
                          className="text-xs px-3 py-1.5 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-[hsl(var(--app-text-secondary))] outline-none"
                        >
                          {MITIGATION_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ").charAt(0).toUpperCase() + s.replace("_", " ").slice(1)}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                );
              })
          )}
        </div>
      )}
    </div>
  );
};

export default RiskManagement;
